import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link as MuiLink } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { StarOff } from "lucide-react";

import messages from "../../../../assets/locale/messages";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import CompanyHeader from "../../../../components/Company/CompanyViewDetails/Header/CompanyHeader";
import Card from "../../../../components/Card";
import CompanyEditDocuments from "../../../../components/Company/CompanyEditDocuments";
import CompanySubscriptionSection from "../../../../components/Company/Subscription/CompanySubscriptionSection";
import TruncatedWithTooltip from "../../../../components/TruncatedWithTooltip";
import CancelEditRequestModal from "../../../../components/Company/CancelEditRequestModal";
import EditRequestBanner, {
  EDIT_REQUEST_BANNER_STATUS,
  mapBackendStatusToBanner,
} from "../../../../components/Company/EditRequestBanner";
import { UIChip, UIEmptyState, UIProgressBar } from "../../../../components/ui";
import { getSatisfactionBand } from "../../../../components/Dashboard/SatisfactionSection/constants";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { clx } from "../../../../utils/Helpers";
import { isCompanyOwner } from "../../../../utils/Permissions";
import { getMyCompanyRequest } from "../../../../store/CompanyPortal/MyCompany/actions";
import {
  deleteCompanyEditRequestRequest,
  getCompanyEditRequestRequest,
} from "../../../../store/CompanyPortal/Settings/EditProfile/actions";

import "./_Profile.scss";

const EMPTY_VALUE = "-";

const ProfileField = ({ label, value, children, valueClassName }) => (
  <div className="company-portal-profile-field">
    <div className="company-portal-profile-field__label">{label}</div>
    <div
      className={clx("company-portal-profile-field__value", valueClassName)}
    >
      {children ?? value ?? EMPTY_VALUE}
    </div>
  </div>
);

const Profile = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const myCompany = useSelector((state) => state.companyMyCompany.company);
  const editRequestFromSlice = useSelector(
    (state) => state.companyEditRequest.editRequest,
  );
  const editRequestSubmitting = useSelector(
    (state) => state.companyEditRequest.submitting,
  );

  const { shared, registrationReq, dashboard } = messages[lang];
  const title = shared.companyPortalProfile;

  // `key` changes on every visit, also when a notification opens the profile already on
  // screen (e.g. the Super Admin changed the plan, PROLINE-4123).
  const { key: locationKey } = useLocation();

  // Refresh the company from `/companies/my_company` every time we land on
  // the profile page. The saga also mirrors the result onto `auth.login.company`
  // so the NavBar and other consumers stay in sync.
  useEffect(() => {
    if (!login?.id) return;
    dispatch(getMyCompanyRequest());
  }, [dispatch, login?.id, locationKey]);

  const data = login;

  // Banner + header lock: trust only `/my_company` — the edit-request slice
  // can be stale after the server clears the request; we still hydrate the
  // slice in the effect below when this id exists (e.g. cancel flow).
  const activeEditRequest = myCompany?.company_edit_request ?? null;
  const activeEditRequestId = activeEditRequest?.id ?? null;

  const userIsCompanyOwner = isCompanyOwner(login);

  // Hydrate the edit-request slice for owner-only flows (cancel / edit request).
  // Employees lack access to GET /company_edit_requests/:id and would see a 403 toast.
  useEffect(() => {
    if (!userIsCompanyOwner || activeEditRequestId == null) return;
    if (String(editRequestFromSlice?.id) === String(activeEditRequestId)) {
      return;
    }
    dispatch(getCompanyEditRequestRequest({ id: activeEditRequestId }));
  }, [
    dispatch,
    activeEditRequestId,
    editRequestFromSlice?.id,
    userIsCompanyOwner,
  ]);

  const bannerStatus = mapBackendStatusToBanner(activeEditRequest?.status);

  // Local-only dismiss: the BE has no "seen" flag yet, so we just hide the
  // banner for the current session when the user clicks Dismiss. If a new
  // request (different id) comes in, the banner re-appears naturally because
  // this state keys off the id.
  const [dismissedRequestId, setDismissedRequestId] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const isDismissed =
    dismissedRequestId != null &&
    String(dismissedRequestId) === String(activeEditRequestId);

  const handleEditProfile = () => {
    history.push(ROUTE_PATHS.companySettingsProfileEdit);
  };

  const handleDismissBanner = () => {
    if (activeEditRequestId != null) {
      setDismissedRequestId(activeEditRequestId);
    }
  };

  const handleCancelRequest = () => {
    if (bannerStatus !== EDIT_REQUEST_BANNER_STATUS.PENDING) return;
    if (activeEditRequestId == null) return;
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    if (editRequestSubmitting) return;
    setCancelDialogOpen(false);
  };

  const handleConfirmCancelRequest = () => {
    if (activeEditRequestId == null) return;
    dispatch(
      deleteCompanyEditRequestRequest({
        id: activeEditRequestId,
        onSuccess: () => {
          setCancelDialogOpen(false);
          dispatch(getMyCompanyRequest());
        },
      }),
    );
  };

  const shouldShowBanner = bannerStatus && !isDismissed;

  // Lock the top-right Edit Profile button whenever ANY active request
  // exists. Rationale:
  // - pending: the banner already exposes its own `Edit Request` action, so
  //   a second top-right button is duplication.
  // - in_review / approved / rejected: the BE rejects update/delete outright.
  // One entry point per state keeps the UX unambiguous.
  const isEditProfileLocked = bannerStatus != null;
  const company = myCompany || data?.company || {};
  const city =
    lang === "ar"
      ? company.city?.name_ar || company.city?.name_en
      : company.city?.name_en || company.city?.name_ar;

  // Contact person is always the company owner — never the logged-in user
  // (an employee may be viewing this page). Mirror the admin OverviewTab and
  // the edit form, which both read `company.company_owner`.
  const owner = company.company_owner || {};
  const phoneNumber = owner.phone_number
    ? `${owner?.country?.dial_code || ""}${owner.phone_number}`
    : EMPTY_VALUE;

  // PROLINE-3868/3873: the same figures as a company's card on the Admin
  // Portal's dashboard — `null` score means no survey has been submitted yet,
  // never `0`; the two counts are always present.
  const overallSatisfaction = company.overall_clients_satisfaction;
  const evaluatingClients = company.number_of_evaluating_clients ?? 0;
  const evaluatedEvents = company.number_of_evaluated_events ?? 0;
  const satisfactionBand =
    overallSatisfaction != null
      ? getSatisfactionBand(Math.round(overallSatisfaction))
      : null;

  return (
    <div className="company-portal-profile company-view-details">
      <Breadcrumbs
        list={[
          {
            path: "",
            label: shared.settings,
          },
          {
            path: "",
            label: title,
          },
        ]}
        BreadCrumbHasBackIcon={false}
      />
      <CompanyHeader
        company={data?.company}
        hasJoinedDate
        onEditProfile={handleEditProfile}
        hasEditProfileButton={userIsCompanyOwner}
        editProfileDisabled={isEditProfileLocked}
        editProfileDisabledTooltip={shared.editRequestBanner?.editProfileLocked}
      />

      {shouldShowBanner && (
        <EditRequestBanner
          status={bannerStatus}
          loading={editRequestSubmitting}
          onDismiss={handleDismissBanner}
          onEdit={userIsCompanyOwner ? handleEditProfile : undefined}
          onCancel={userIsCompanyOwner ? handleCancelRequest : undefined}
        />
      )}

      <CancelEditRequestModal
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        onConfirm={handleConfirmCancelRequest}
        submitting={editRequestSubmitting}
      />

      <CompanySubscriptionSection company={company} />

      <div className="company-portal-profile__layout">
        <div className="company-portal-profile__content">
          <div className="company-portal-profile__main">
            <Card
              title={registrationReq.companyOverallSatisfaction}
              classNames={{ body: "company-portal-profile-card__body" }}
            >
              {overallSatisfaction != null ? (
                <div className="company-portal-profile-satisfaction">
                  <div className="company-portal-profile-satisfaction__score-row">
                    <span className="company-portal-profile-satisfaction__label">
                      {dashboard.satisfaction.overall}
                    </span>
                    <span className="company-portal-profile-satisfaction__value">
                      <span>{Math.round(overallSatisfaction)}</span>
                      <span>%</span>
                      <span className="company-portal-profile-satisfaction__period">
                        · {registrationReq.companySatisfactionPeriod}
                      </span>
                    </span>
                  </div>

                  <UIProgressBar
                    value={Math.round(overallSatisfaction)}
                    color={satisfactionBand.tone}
                    size="sm"
                    trackColor="var(--color-primary-0, #F3F0FF)"
                  />

                  <div className="company-portal-profile-satisfaction__footer">
                    <span className="company-portal-profile-satisfaction__count">
                      {(evaluatingClients === 1
                        ? dashboard.companySatisfaction.clientsOne
                        : dashboard.companySatisfaction.clients
                      ).replace("{count}", evaluatingClients)}
                      {" · "}
                      {(evaluatedEvents === 1
                        ? dashboard.companySatisfaction.eventsOne
                        : dashboard.companySatisfaction.events
                      ).replace("{count}", evaluatedEvents)}
                    </span>
                    <UIChip
                      appearance="light"
                      size="small"
                      color={satisfactionBand.chipColor}
                      label={dashboard.satisfaction.bands[satisfactionBand.chipLabelKey]}
                    />
                  </div>
                </div>
              ) : (
                <UIEmptyState
                  icon={<StarOff />}
                  title={dashboard.satisfaction.empty.title}
                  description={dashboard.satisfaction.empty.description}
                  classNames={{ root: "ui-empty-state--embedded" }}
                />
              )}
            </Card>
            <Card
              title={registrationReq.companyInformation}
              classNames={{ body: "company-portal-profile-card__body" }}
            >
              <div className="company-portal-profile-field-grid company-portal-profile-field-grid--cols-2">
                <ProfileField label={registrationReq.englishName}>
                  <TruncatedWithTooltip
                    title={company.name_en || EMPTY_VALUE}
                    lines={2}
                  >
                    <span>{company.name_en || EMPTY_VALUE}</span>
                  </TruncatedWithTooltip>
                </ProfileField>
                <ProfileField label={registrationReq.arabicName}>
                  <TruncatedWithTooltip
                    title={company.name_ar || EMPTY_VALUE}
                    lines={2}
                  >
                    <span>{company.name_ar || EMPTY_VALUE}</span>
                  </TruncatedWithTooltip>
                </ProfileField>
                <ProfileField
                  label={registrationReq.city}
                  value={city || EMPTY_VALUE}
                />
                <ProfileField label={registrationReq.commercialRecord}>
                  {company.commercial_record_url ? (
                    <MuiLink
                      href={company.commercial_record_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {company.commercial_record || EMPTY_VALUE}
                    </MuiLink>
                  ) : (
                    company.commercial_record || EMPTY_VALUE
                  )}
                </ProfileField>
              </div>
            </Card>
            <Card
              title={registrationReq.contactPerson}
              classNames={{ body: "company-portal-profile-card__body" }}
            >
              <div className="company-portal-profile-field-grid company-portal-profile-field-grid--cols-3">
                <ProfileField
                  label={registrationReq.fullName}
                  value={owner.name || EMPTY_VALUE}
                />
                <ProfileField
                  label={registrationReq.mobileNumber}
                  value={phoneNumber}
                  valueClassName="company-portal-profile-field__value--phone"
                />
                <ProfileField label={registrationReq.emailAddress}>
                  {owner.email ? (
                    <MuiLink href={`mailto:${owner.email}`} underline="hover">
                      {owner.email}
                    </MuiLink>
                  ) : (
                    EMPTY_VALUE
                  )}
                </ProfileField>
              </div>
            </Card>
          </div>
        </div>

        <aside className="company-portal-profile__aside">
          <CompanyEditDocuments
            commercialRecordUrl={company.commercial_record_url}
            delegationDocumentUrl={owner.delegation_document_url}
            idDocumentUrl={owner.personal_id_url}
            pendingDocs={{}}
            onDocumentChange={() => {}}
            showReplace={false}
            showMissingDocuments={false}
          />
        </aside>
      </div>
    </div>
  );
};

ProfileField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  children: PropTypes.node,
  valueClassName: PropTypes.string,
};

ProfileField.defaultProps = {
  value: EMPTY_VALUE,
  children: null,
  valueClassName: "",
};

export default Profile;
